function isoWeekNumber(dateStr: string): number | null {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function isoWeekYear(dateStr: string): number | null {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  return tmp.getUTCFullYear();
}

const ISO_WEEK_LABEL = /^W(\d{1,2})-(\d{4})$/;
const DAY_LABEL = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_LABEL = /^\d{4}-\d{2}$/;
const YEAR_LABEL = /^\d{4}$/;

/** True when a chart/metrics label is a day, ISO week, month, or year bucket. */
export function isTemporalCategoryLabel(label: string): boolean {
  return (
    ISO_WEEK_LABEL.test(label) ||
    DAY_LABEL.test(label) ||
    MONTH_LABEL.test(label) ||
    YEAR_LABEL.test(label)
  );
}

/** Monday–Sunday bounds for an ISO week label (`W12-2026`). */
export function isoWeekToDateRange(
  weekLabel: string,
): { from: string; to: string } | null {
  const match = weekLabel.match(ISO_WEEK_LABEL);
  if (!match) return null;
  const week = parseInt(match[1], 10);
  const isoYear = parseInt(match[2], 10);
  if (week < 1 || week > 53) return null;

  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const mondayWeek1 = new Date(jan4);
  mondayWeek1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const monday = new Date(mondayWeek1);
  monday.setUTCDate(mondayWeek1.getUTCDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return {
    from: monday.toISOString().slice(0, 10),
    to: sunday.toISOString().slice(0, 10),
  };
}

/**
 * Human-readable Mon–Sun span for an ISO week label,
 * e.g. "W29-2026" → "Jul 13 – Jul 19, 2026".
 */
export function formatWeekRange(weekLabel: string): string | null {
  const range = isoWeekToDateRange(weekLabel);
  if (!range) return null;
  const from = new Date(`${range.from}T00:00:00Z`);
  const to = new Date(`${range.to}T00:00:00Z`);
  const fmtDate = (d: Date) =>
    d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  const fromYear = from.getUTCFullYear();
  const toYear = to.getUTCFullYear();
  if (fromYear === toYear) {
    return `${fmtDate(from)} – ${fmtDate(to)}, ${toYear}`;
  }
  return `${fmtDate(from)}, ${fromYear} – ${fmtDate(to)}, ${toYear}`;
}

/** Tooltip title for a temporal x-axis category (actual dates, not week codes). */
export function formatTemporalCategoryTooltip(category: string): string {
  const week = formatWeekRange(category);
  if (week) return week;
  if (MONTH_LABEL.test(category)) {
    const d = parseFlexibleDate(`${category}-01`);
    if (d) {
      return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
  }
  if (DAY_LABEL.test(category)) {
    return formatDatePlain(category);
  }
  return category;
}

/** Compact axis tick for a temporal bucket (`W29-2026` → `Jul 13`, `2026-03` → `Mar`). */
export function formatTemporalAxisLabel(category: string): string {
  const week = isoWeekToDateRange(category);
  if (week) return formatDateShort(week.from) ?? category;
  if (MONTH_LABEL.test(category)) {
    const d = parseFlexibleDate(`${category}-01`);
    if (d) return d.toLocaleDateString(undefined, { month: "short" });
  }
  if (DAY_LABEL.test(category)) return formatDateShort(category) ?? category;
  return category;
}

/**
 * Compact inclusive date span, e.g. `Jul 26 – Sep 19`. Same-month ranges
 * collapse the repeated month (`Jul 26 – 31`); cross-year ranges keep years.
 */
export function formatCompactDateRange(
  from?: string | null,
  to?: string | null,
): string | null {
  if (!from && !to) return null;
  if (from && !to) {
    const start = formatDateShort(from);
    return start ? `${start} –` : null;
  }
  if (!from && to) {
    const end = formatDateShort(to);
    return end ? `– ${end}` : null;
  }
  const start = parseFlexibleDate(from!);
  const end = parseFlexibleDate(to!);
  if (!start || !end) return null;
  if (from === to) return formatDateShort(from);

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const monthDay = (d: Date) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (sameMonth) {
    return `${monthDay(start)} – ${end.getDate()}`;
  }
  if (sameYear) {
    return `${monthDay(start)} – ${monthDay(end)}`;
  }
  return `${formatDatePlain(from)} – ${formatDatePlain(to)}`;
}

/** ISO week label e.g. "W11-2026". */
export function toWeekLabel(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const wn = isoWeekNumber(dateStr);
  const yr = isoWeekYear(dateStr);
  if (wn == null || yr == null) return null;
  return `W${String(wn).padStart(2, "0")}-${yr}`;
}

export function formatDatePlain(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export type DateDisplayParts =
  | { kind: "plain"; text: string }
  | { kind: "withWeek"; date: string; week: string };

/** Split date vs ISO week for stacked UI; omits calendar year when week carries it. */
export function getDateDisplayParts(
  dateStr: string | null | undefined,
  showWeek: boolean,
): DateDisplayParts {
  if (!dateStr) return { kind: "plain", text: "—" };
  if (!showWeek) return { kind: "plain", text: formatDatePlain(dateStr) };

  const week = toWeekLabel(dateStr);
  const day = formatDateShort(dateStr);
  if (!day) return { kind: "plain", text: "—" };
  if (!week) return { kind: "plain", text: formatDatePlain(dateStr) };
  return { kind: "withWeek", date: day, week };
}

export function formatDateWithWeek(dateStr: string | null | undefined): string {
  const parts = getDateDisplayParts(dateStr, true);
  if (parts.kind === "plain") return parts.text;
  return `${parts.date} · ${parts.week}`;
}

function parseFlexibleDate(dateStr: string): Date | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    const local = new Date(year, month - 1, day);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const parsed = new Date(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Compact month + day for steppers, e.g. "Mar 14". */
export function formatDateShort(
  dateStr: string | null | undefined,
): string | null {
  if (!dateStr) return null;
  const d = parseFlexibleDate(dateStr);
  if (!d) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Date + time for popovers, e.g. "Mar 14, 2026, 3:42 PM". */
export function formatDateTimePlain(
  dateStr: string | null | undefined,
): string {
  if (!dateStr) return "—";
  const d = parseFlexibleDate(dateStr);
  if (!d) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
